import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { 
  Shield, ArrowLeft, Save, Plus, X, Loader2, Settings, Factory, Wrench, HardDrive
} from "lucide-react";
import { toast } from "sonner";
import { getAuthHeaders } from "@/utils/auth";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function AdminSettings() {
  const navigate = useNavigate();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newManufacturer, setNewManufacturer] = useState("");
  const [newToolType, setNewToolType] = useState("");

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await fetch(`${API}/admin/settings`, {
        headers: getAuthHeaders()
      });

      if (!response.ok) {
        if (response.status === 403) {
          toast.error("Admin access required");
          navigate("/dashboard");
          return;
        }
        throw new Error("Failed to fetch settings");
      }

      const data = await response.json();
      setSettings(data);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API}/admin/settings`, {
        method: "PUT",
        headers: { 
          "Content-Type": "application/json", 
          ...getAuthHeaders() 
        },
        body: JSON.stringify(settings)
      });

      if (response.ok) {
        toast.success("Settings saved");
      } else {
        throw new Error("Failed to save settings");
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  const addManufacturer = () => {
    if (!newManufacturer.trim()) return;
    const upper = newManufacturer.trim().toUpperCase();
    if (settings.allowed_manufacturers.includes(upper)) {
      toast.error("Manufacturer already exists");
      return;
    }
    setSettings({
      ...settings,
      allowed_manufacturers: [...settings.allowed_manufacturers, upper].sort()
    });
    setNewManufacturer("");
  };

  const removeManufacturer = (mfg) => {
    setSettings({
      ...settings,
      allowed_manufacturers: settings.allowed_manufacturers.filter(m => m !== mfg)
    });
  };

  const addToolType = () => {
    if (!newToolType.trim()) return;
    const type = newToolType.trim();
    if (settings.tool_types.includes(type)) {
      toast.error("Tool type already exists");
      return;
    }
    setSettings({
      ...settings,
      tool_types: [...settings.tool_types, type].sort()
    });
    setNewToolType("");
  };

  const removeToolType = (type) => {
    setSettings({
      ...settings,
      tool_types: settings.tool_types.filter(t => t !== type)
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-card/50 backdrop-blur-sm sticky top-0 z-40">
        <div className="flex items-center justify-between px-4 py-3 lg:px-8">
          <div className="flex items-center gap-4">
            <Link to="/admin">
              <Button variant="ghost" size="sm">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Admin
              </Button>
            </Link>
            <div className="flex items-center gap-2">
              <Shield className="w-6 h-6 text-primary" />
              <span className="font-heading text-lg font-bold uppercase">System Settings</span>
            </div>
          </div>
          <Button 
            onClick={handleSave} 
            disabled={saving}
            className="btn-primary"
            data-testid="save-settings-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
            Save Changes
          </Button>
        </div>
      </header>

      <main className="p-4 lg:p-8 max-w-4xl mx-auto space-y-6">
        {/* Manufacturers */}
        <Card className="border-border bg-card">
          <CardHeader>
            <CardTitle className="font-heading uppercase text-lg flex items-center gap-2">
              <Factory className="w-5 h-5 text-primary" />
              Allowed Manufacturers
            </CardTitle>
            <CardDescription>
              These manufacturers are recognized by the OCR scanner for auto-detection
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex gap-2">
              <Input
                placeholder="Add new manufacturer..."
                value={newManufacturer}
                onChange={(e) => setNewManufacturer(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && addManufacturer()}
                className="bg-background flex-1"
                data-testid="new-manufacturer-input"
              />
              <Button onClick={addManufacturer} size="sm" className="btn-primary">
                <Plus className="w-4 h-4" />
              </Button>
            </div>
            <div className="flex flex-wrap gap-2">
              {settings?.allowed_manufacturers?.map((mfg) => (
                <Badge 
                  key={mfg} 
                  variant="secondary" 
                  className="pr-1 flex items-center gap-1"
                >
                  {mfg}
                  <button 
                    onClick={() => removeManufacturer(mfg)}
                    className="ml-1 hover:bg-destructive/20 rounded p-0.5"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </Badge>
              ))}
            </div>
            <p className="text-xs text-muted-foreground">
              {settings?.allowed_manufacturers?.length || 0} manufacturers configured
            </p>
          </CardContent>
        </Card>

        {/* Tool Types */}
        <Card className="border-border bg-card">
          <CardHeader>
            <CardTitle className="font-heading uppercase text-lg flex items-center gap-2">
              <Wrench className="w-5 h-5 text-primary" />
              Tool Types
            </CardTitle>
            <CardDescription>
              Available tool categories for users to select when adding tools
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex gap-2">
              <Input
                placeholder="Add new tool type..."
                value={newToolType}
                onChange={(e) => setNewToolType(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && addToolType()}
                className="bg-background flex-1"
                data-testid="new-tool-type-input"
              />
              <Button onClick={addToolType} size="sm" className="btn-primary">
                <Plus className="w-4 h-4" />
              </Button>
            </div>
            <div className="flex flex-wrap gap-2">
              {settings?.tool_types?.map((type) => (
                <Badge 
                  key={type} 
                  variant="secondary" 
                  className="pr-1 flex items-center gap-1"
                >
                  {type}
                  <button 
                    onClick={() => removeToolType(type)}
                    className="ml-1 hover:bg-destructive/20 rounded p-0.5"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </Badge>
              ))}
            </div>
            <p className="text-xs text-muted-foreground">
              {settings?.tool_types?.length || 0} tool types configured
            </p>
          </CardContent>
        </Card>

        {/* File Size Limits */}
        <Card className="border-border bg-card">
          <CardHeader>
            <CardTitle className="font-heading uppercase text-lg flex items-center gap-2">
              <HardDrive className="w-5 h-5 text-primary" />
              File Upload Limits
            </CardTitle>
            <CardDescription>
              Maximum file sizes for user uploads
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="profile-size">Profile Image Size (MB)</Label>
                <Input
                  id="profile-size"
                  type="number"
                  min="1"
                  max="20"
                  value={settings?.max_profile_image_size_mb || 5}
                  onChange={(e) => setSettings({
                    ...settings,
                    max_profile_image_size_mb: parseInt(e.target.value) || 5
                  })}
                  className="bg-background"
                  data-testid="profile-size-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="tool-size">Tool Scan Image Size (MB)</Label>
                <Input
                  id="tool-size"
                  type="number"
                  min="1"
                  max="50"
                  value={settings?.max_tool_image_size_mb || 10}
                  onChange={(e) => setSettings({
                    ...settings,
                    max_tool_image_size_mb: parseInt(e.target.value) || 10
                  })}
                  className="bg-background"
                  data-testid="tool-size-input"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Info Card */}
        <Card className="border-primary/30 bg-primary/5">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <Settings className="w-5 h-5 text-primary mt-0.5" />
              <div>
                <p className="font-medium text-sm">Configuration Notes</p>
                <ul className="text-xs text-muted-foreground mt-1 space-y-1">
                  <li>• Changes to manufacturers affect OCR auto-detection accuracy</li>
                  <li>• Tool types appear in the dropdown when users add new tools</li>
                  <li>• File size limits are enforced on both frontend and backend</li>
                  <li>• All changes are logged in the admin activity log</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
